import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { SlidersHorizontal, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtApiError, fmtFecha } from "@/lib/api";
import { hoyISOBA } from "@/components/ClockBA";
import { Badge, Card, MesNav, Modal, PageHeader, btnSec, inputCls } from "@/components/rrhh/ui";

export default function AjustesPage() {
  const qc = useQueryClient();
  const [mes, setMes] = useState(hoyISOBA().slice(0, 7));
  const [empleadoId, setEmpleadoId] = useState("");
  const [deshacer, setDeshacer] = useState(null);

  const { data: empleados } = useQuery({ queryKey: ["empleados"], queryFn: async () => (await api.get("/empleados")).data });
  const { data: ajustes, isLoading } = useQuery({
    queryKey: ["liq-ajustes", mes, empleadoId],
    queryFn: async () => (await api.get("/liquidacion/ajustes", {
      params: { mes, empleado_id: empleadoId || undefined },
    })).data,
    refetchInterval: 8000,
  });

  const mutDeshacer = useMutation({
    mutationFn: async (id) => api.post(`/liquidacion/ajustes/${id}/deshacer`),
    onSuccess: () => {
      setDeshacer(null);
      qc.invalidateQueries({ queryKey: ["liq-ajustes"] });
      qc.invalidateQueries({ queryKey: ["liq-resumen"] });
      qc.invalidateQueries({ queryKey: ["liq-persona"] });
      toast.success("Ajuste deshecho — el día volvió al cálculo automático");
    },
    onError: (e) => toast.error(fmtApiError(e)),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-ajustes">
      <PageHeader
        titulo="Ajustes"
        subtitulo="Todo lo que se cambió a mano este mes, con su motivo."
        testid="header-ajustes"
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <MesNav mes={mes} onChange={setMes} testid="nav-mes-ajustes" />
        <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} className={inputCls} data-testid="filtro-persona-ajustes">
          <option value="">Todas las personas</option>
          {empleados?.map((e) => <option key={e.id} value={e.id}>{e.apellido}, {e.nombre} ({e.legajo})</option>)}
        </select>
      </div>

      <Card testid="card-ajustes" bodyCls="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Fecha y hora del ajuste</th>
                <th className="px-4 py-3 font-semibold">Persona</th>
                <th className="px-4 py-3 font-semibold">Día ajustado</th>
                <th className="px-4 py-3 font-semibold">Campo</th>
                <th className="px-4 py-3 font-semibold">Antes → Después</th>
                <th className="px-4 py-3 font-semibold">Justificación</th>
                <th className="px-4 py-3 font-semibold">Usuario</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-5 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={9} className="px-5 py-10 text-center text-gray-500">Cargando…</td></tr>}
              {ajustes?.flatMap((a) =>
                a.campos.map((c, i) => (
                  <tr key={`${a.id}-${i}`} className={`border-b border-white/5 last:border-0 ${a.deshecho ? "opacity-45" : ""}`} data-testid={`ajuste-${a.id.slice(0, 8)}`}>
                    {i === 0 && (
                      <>
                        <td className="whitespace-nowrap px-5 py-3 font-mono-num text-xs text-gray-400" rowSpan={a.campos.length}>
                          {fmtFecha(a.fecha_hora)} {a.fecha_hora.slice(11, 16)}
                        </td>
                        <td className="px-4 py-3" rowSpan={a.campos.length}>
                          <span className="text-sm font-medium text-white">{a.empleado_nombre}</span>{" "}
                          <span className="font-mono-num text-xs text-gray-500">({a.legajo})</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono-num text-xs text-gray-300" rowSpan={a.campos.length}>{fmtFecha(a.fecha)}</td>
                      </>
                    )}
                    <td className="px-4 py-3 text-[13px] text-gray-300">{c.campo}</td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono-num text-xs">
                      <span className="text-gray-500">{c.antes}</span> <span className="text-gray-600">→</span> <span className="font-semibold text-[#2DD4BF]">{c.despues}</span>
                    </td>
                    {i === 0 && (
                      <>
                        <td className="max-w-64 px-4 py-3 text-xs italic text-gray-400" rowSpan={a.campos.length}>“{a.justificacion}”</td>
                        <td className="px-4 py-3" rowSpan={a.campos.length}>
                          <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-gray-300">{a.usuario}</span>
                        </td>
                        <td className="px-4 py-3" rowSpan={a.campos.length}>
                          <Badge color={a.deshecho ? "gris" : "verde"}>{a.deshecho ? "Deshecho" : "Vigente"}</Badge>
                        </td>
                        <td className="px-5 py-3 text-right" rowSpan={a.campos.length}>
                          {!a.deshecho && (
                            <button className={`${btnSec} !px-2.5 !py-1 text-xs`} onClick={() => setDeshacer(a)} data-testid={`deshacer-${a.id.slice(0, 8)}`}>
                              <Undo2 size={12} className="mr-1 inline" /> Deshacer
                            </button>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
              {ajustes?.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center">
                    <SlidersHorizontal size={26} className="mx-auto text-gray-600" />
                    <p className="mt-3 text-sm font-semibold text-gray-300">No hay ajustes en este mes</p>
                    <p className="text-xs text-gray-500">Cuando ajustes un día desde el detalle de una persona, aparece acá.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {deshacer && (
        <Modal titulo="Deshacer el ajuste" onClose={() => setDeshacer(null)} testid="modal-deshacer">
          <p className="mb-1 text-sm text-gray-300">
            <b className="text-white">{deshacer.empleado_nombre}</b> — día {fmtFecha(deshacer.fecha)}
          </p>
          <p className="mb-4 text-sm text-gray-400">
            El día vuelve al cálculo automático
            {deshacer.tipo === "fichada_manual" && " y la fichada manual queda anulada"}. El deshacer también queda en el historial.
          </p>
          <div className="flex justify-end gap-2">
            <button className={btnSec} onClick={() => setDeshacer(null)}>Cancelar</button>
            <button
              className="rounded-xl bg-red-400 px-4 py-2 text-sm font-bold text-black transition-all hover:brightness-110 disabled:opacity-40"
              disabled={mutDeshacer.isPending}
              onClick={() => mutDeshacer.mutate(deshacer.id)}
              data-testid="confirmar-deshacer"
            >
              Deshacer
            </button>
          </div>
        </Modal>
      )}
    </motion.div>
  );
}
